import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  createStylesParam,
} from '@wix/tpa-settings';

type IStylesParams = {
  backgroundColor: StyleParamType.Color;
  textFont: StyleParamType.Font;
  textColor: StyleParamType.Color;
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  formFieldsGap: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  selectionColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColorAndOpacity: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderColorAndOpacity: StyleParamType.Color;
  widgetBorderRadius: StyleParamType.Number;
  formBorderWidth: StyleParamType.Number;
  formBorderColor: StyleParamType.Color;
  formBorderRadius: StyleParamType.Number;
  formFieldBackgroundColor: StyleParamType.Color;
  sectionTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  formFieldsLabelsColor: StyleParamType.Color;
  formFieldsLabelsFont: StyleParamType.Font;
  formFieldsInputTextColor: StyleParamType.Color;
  formFieldsInputTextFont: StyleParamType.Font;
  mobileServiceNameFontSize: StyleParamType.Number;
  mobileServiceTitlesFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileFormFieldsLabelsFontSize: StyleParamType.Number;
  mobileFormFieldsInputsFontSize: StyleParamType.Number;
  mobileButtonSize: StyleParamType.Number;
  mainButtonTextColor: StyleParamType.Color;
  mainButtonFont: StyleParamType.Font;
  mainButtonBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonBorderColorAndOpacity: StyleParamType.Color;
  mainButtonBorderWidth: StyleParamType.Number;
  mainButtonBorderRadius: StyleParamType.Number;
  mainButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonHoverColorAndOpacity: StyleParamType.Color;
  mainButtonHoverTextColor: StyleParamType.Color;
  secondaryButtonTextColor: StyleParamType.Color;
  secondaryButtonFont: StyleParamType.Font;
  secondaryButtonBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderWidth: StyleParamType.Number;
  secondaryButtonBorderRadius: StyleParamType.Number;
  secondaryButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverTextColor: StyleParamType.Color;
  formHeaderTwoFont: StyleParamType.Font;
  formHeaderTwoColor: StyleParamType.Color;
  formTextFont: StyleParamType.Font;
  formTextColor: StyleParamType.Color;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  serviceDetailsFont: StyleParamType.Font;
  serviceDetailsColor: StyleParamType.Color;
  secondaryTextFormFont: StyleParamType.Font;
  secondaryTextFormColor: StyleParamType.Color;
  disableColor: StyleParamType.Color;
};

const widgetBackgroundColor = createStylesParam('widgetBackgroundColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-1'),
});

const mainButtonBorderWidth = createStylesParam('mainButtonBorderWidth', {
  type: StyleParamType.Number,
  getDefaultValue: () => 1,
});

const mainButtonBorderRadius = createStylesParam('mainButtonBorderRadius', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
});

const primaryTextColor = createStylesParam('primaryTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
});

const primaryTextFont = createStylesParam('primaryTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', { size: 16 }),
});

const sectionTitleFont = createStylesParam('sectionTitleFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Page-title', { size: 20 }),
});

const sectionTitleColor = createStylesParam('sectionTitleColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
});

const secondaryTextFont = createStylesParam('secondaryTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', { size: 14 }),
});

const secondaryTextColor = createStylesParam('secondaryTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-4'),
});

export default createStylesParams<IStylesParams>({
  backgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  textFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
  },
  textColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  topAndBottomPadding: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: () => 48,
  },
  sidePadding: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: () => 20,
  },
  formFieldsGap: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: () => 24,
  },
  widgetBackgroundColor,
  selectionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  dividerColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  widgetBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  widgetBorderColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  widgetBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  formBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  formBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  formBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  formFieldBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(widgetBackgroundColor) ?? wixColorParam('color-1'),
  },
  sectionTitleFont,
  sectionTitleColor,
  primaryTextFont,
  primaryTextColor,
  secondaryTextColor,
  secondaryTextFont,
  formFieldsLabelsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  formFieldsLabelsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  formFieldsInputTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  formFieldsInputTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  mobileServiceNameFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  mobileServiceTitlesFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  mobilePrimaryTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileSecondaryTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileFormFieldsLabelsFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileFormFieldsInputsFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileButtonSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mainButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  mainButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  mainButtonBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mainButtonBorderColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  mainButtonBorderWidth,
  mainButtonBorderRadius,
  mainButtonHoverBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
  },
  mainButtonHoverColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 1),
  },
  mainButtonHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  secondaryButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  secondaryButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  secondaryButtonBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  secondaryButtonBorderColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  secondaryButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(mainButtonBorderWidth) ?? 1,
  },
  secondaryButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(mainButtonBorderRadius) ?? 0,
  },
  secondaryButtonHoverBackgroundColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
  },
  secondaryButtonHoverColorAndOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
  },
  secondaryButtonHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  formHeaderTwoFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(sectionTitleFont) ??
      wixFontParam('Body-M', { size: 20 }),
  },
  formHeaderTwoColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(sectionTitleColor) ?? wixColorParam('color-5'),
  },
  formTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue, isMobile }) =>
      getStyleParamValue(primaryTextFont) ??
      wixFontParam('Body-M', { size: isMobile ? 16 : 14 }),
  },
  formTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(primaryTextColor) ?? wixColorParam('color-5'),
  },
  pageTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 32 }),
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  serviceDetailsFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(sectionTitleFont) ??
      wixFontParam('Page-title', { size: 20 }),
  },
  serviceDetailsColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(sectionTitleColor) ?? wixColorParam('color-5'),
  },
  secondaryTextFormFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(secondaryTextFont) ??
      wixFontParam('Body-M', { size: 14 }),
  },
  secondaryTextFormColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(secondaryTextColor) ??
      wixColorParam('color-text-secondary'),
  },
  disableColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary-disabled', 1),
  },
});
